import { useCallback } from 'react';
import { Box } from '@mui/material';
import {
  AddCuboidButton,
  HidePointsOutsideCuboidControl,
  CuboidTransformButtonGroup,
  CuboidSaveButtonGroup,
  VerticalDivider,
  HideCuboidButtonGroup,
} from '@/modules/cuboids/components/CuboidToolbar';
import { CuboidClassSelector } from './CuboidClassSelector';
import { useCuboidState } from '../hooks/useCuboidState';
import { useCuboidDataHandlers } from '../hooks/useCuboidDataHandlers';
import { useAtom } from 'jotai';
import { useCuboidStyleHandlers } from '../hooks/useCuboidStyleHandlers';
import { CuboidAdjustButtonGroup } from '@/modules/cuboids/components/CuboidToolbar/CuboidAdjustButtonGroup';
import { useMakeStyledCuboid } from '@/modules/cuboids/hooks/useMakeStyledCuboid';
import * as state from '../state';

export const CuboidToolbar = () => {
  const cuboidState = useCuboidState();
  const dataHandlers = useCuboidDataHandlers();
  const styleHandlers = useCuboidStyleHandlers();

  const [currentCuboid, setCurrentCuboid] = useAtom(cuboidState.currentCuboid);

  const makeStyledCuboid = useMakeStyledCuboid(styleHandlers);

  const setCurrentCuboidClassId = useCallback(
    (cuboidClassId: string) => {
      if (!currentCuboid) return;
      if (currentCuboid.cuboidData.cuboidClassId === cuboidClassId) return;

      const updatedCuboidData = { ...currentCuboid.cuboidData, cuboidClassId };

      const { position, rotation, scale } = currentCuboid;
      const updatedCuboid = {
        ...currentCuboid,
        ...makeStyledCuboid(updatedCuboidData),
        position,
        rotation,
        scale,
      };
      setCurrentCuboid(updatedCuboid);
      dataHandlers.addUpdateCuboid(updatedCuboidData);
    },
    [currentCuboid, setCurrentCuboid, dataHandlers, makeStyledCuboid]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row' }}>
      <AddCuboidButton cuboidState={cuboidState} />

      {currentCuboid && (
        <>
          <Box sx={{ marginLeft: 5 }}>
            <CuboidClassSelector
              cuboidClassId={currentCuboid.cuboidData.cuboidClassId}
              setCuboidClassId={setCurrentCuboidClassId}
            />
          </Box>
          <HidePointsOutsideCuboidControl cuboidState={cuboidState} />

          <VerticalDivider />

          <HideCuboidButtonGroup
            cuboidState={cuboidState}
            hiddenCuboidIds={state.hiddenCuboidIds}
          />

          <VerticalDivider />

          <CuboidTransformButtonGroup cuboidState={cuboidState} />

          <VerticalDivider />

          <CuboidAdjustButtonGroup cuboidState={cuboidState} />

          <VerticalDivider />

          <CuboidSaveButtonGroup cuboidState={cuboidState} dataHandlers={dataHandlers} />
        </>
      )}
    </Box>
  );
};
